import { FlapHeader, Input, IconBtn, WithLoadingComponent, ProgressLoader, CustomAccordion, ToastNotification } from 'components';
import { ConfirmPopup } from "primereact/confirmpopup";
import React from 'react';
import { useHistory } from 'react-router';
import utils, { CUSTOM_MESSAGES } from "utils";
import { FolderService } from "services/http";
import { TaskTagTemplate, TaskStatusTemplate } from "components/pages/client-management/client-history";
import "components/pages/admin-page/folder-management/style.scss";
import { FolderFormTemplate } from 'components/pages';

/**
 * Folder management main view
 * @returns React.Component
 */
export const FolderManagementView = () => {

    const history = useHistory();
    const [searchVal, setSearchVal] = React.useState();
    const [displayFolderForm, setDisplayFolderForm] = React.useState(false);
    const [formTitle, setFormTitle] = React.useState();
    const [refresh, setRefresh] = React.useState();
    const toastRef = React.useRef();
    const [folderPayload, setFolderPayload] = React.useState({});

    return (
        <>
            <FlapHeader
                primeary
                justify
                leftSide={<span className="pt-1">Folder management</span>}
                rightSide={
                    <div className="d-flex">
                        <Input
                            className="mr-2"
                            icon="bi bi-search"
                            placeholder="Search..."
                            value={searchVal}
                            onChange={(e) => { utils.pushURLSearchParams(history, "search", e.target.value); setSearchVal(e.target.value) }} />
                        <IconBtn
                            icon="bi bi-plus-circle"
                            className="my-1"
                            tooltip="Add new folder"
                            size="18px"
                            onClick={() => {
                                setDisplayFolderForm(true);
                                setFolderPayload({});
                                setFormTitle("Add New Folder");
                            }} />
                    </div>
                } />
            <div className="scroll-container" style={{ height: "80vh" }}>
                <FolderSetHoc
                    shouldRefresh={refresh}
                    editAction={(payload) => {
                        setFolderPayload(payload);
                        setDisplayFolderForm(true);
                        setFormTitle("Update folder")
                    }} />
            </div>
            <FolderFormTemplate
                title={formTitle}
                display={displayFolderForm}
                folder={folderPayload}
                hideFnc={() => setDisplayFolderForm(false)}
                successCallback={(msg) => { toastRef.current?.showToast({ type: 'success', title: 'Callbak Response', message: msg }); setRefresh(new Date().getMilliseconds()) }}
                errorCallback={(error) => toastRef.current?.showToast({ type: 'warn', title: 'Callbak Response', message: error })}
            />
            <ToastNotification ref={toastRef} />
        </>
    );
}

/**
 * Folder grid collection
 * @param {Object} props {data, editAction} 
 * @returns React.Component
 */
const FolderSetContainer = (props) => {
    const { data, editAction } = props;
    const [folders, setFolders] = React.useState(data);
    const searchItem = new URLSearchParams(window.location.search).get("search");

    /**
     * On folder search callback action
     */
    React.useEffect(() => {
        setFolders(searchItem ? data.filter(folder => folder.description.toUpperCase().includes(searchItem.toUpperCase())) : data);
    }, [searchItem]);

    return <div className="m-4" style={{ display: "grid", gridTemplateColumns: "20% 20% 20% 20% 20%" }} >
        {
            folders.map((folder, index) => {
                return <FolderItemTemplate
                    key={index} {...folder}
                    editAction={() => { editAction(folder) }}
                    deleteAction={(folderId) => setFolders(folders.filter(folder => folder.folderId !== folderId))} />
            })
        }
    </div>
}

/**
 * High Order Component Folders fetch
 * @param {Object} props {shouldRefres}
 * @returns 
 */
const FolderSetHoc = (props) => {
    const { shouldRefresh } = props;
    const FoldersHoc = React.useMemo(() => WithLoadingComponent(FolderSetContainer, () => FolderService.getFolderItems(), <div style={{ position: "relative", minHeight: "90vh" }}><ProgressLoader /></div>)(props), [shouldRefresh]);
    return <FoldersHoc />
}

/**
 * Folder Item Template Component
 * @param {Object} props { folderId, description, icon, visibility, tags, teams, statuses, sources, deleteAction, editAction }
 * @returns React.Component
 */
const FolderItemTemplate = (props) => {
    const { folderId, description, icon, visibility, tags, teams, statuses, sources, deleteAction, editAction } = props;
    const [displayDeleteConfirm, setDisplayDeleteConfirm] = React.useState(false);

    /**
     * On folder delete action
     */
    const onDeleteTag = () => {
        FolderService.deleteFolder({ folderId }).then(() => {
            deleteAction(folderId);
        }, error => {
            alert(error);
        });
    }

    return <div className="card shadow-sm m-2 text-left w-70" title={description}>
        <div className='card-header d-flex justify-content-between py-2'>
            <div>
                <i className={icon} />&nbsp; <span title={description}>{utils.shorthandString(description, Math.trunc(window.screen.width / 120))}</span>
            </div>
        </div>
        <div className="card-body p-2" style={{ position: "relative" }}>
            {
                sources.length ?
                    <CustomAccordion
                        header={<small>Sources</small>}
                        headerClass="folder-accord-header"
                        contentClass="folder-accord-content"
                        content={
                            <div className='row'>
                                {sources.map(source => <i className={`mx-1 ${utils.guessSourceIcon(source.sourceId)}`} title={source.sourceId} style={{ color: "#0d88ef" }} />)}
                            </div>
                        }
                    /> : <></>
            }
            {
                tags.length ? <CustomAccordion
                    header={<small>Tags</small>}
                    headerClass="folder-accord-header"
                    contentClass="folder-accord-content"
                    content={
                        <div className='row'>
                            {tags.map(tag => <TaskTagTemplate value={tag.description} color="#4dadd9" />)}
                        </div>
                    }
                /> : <></>
            }
            {
                statuses.length ? <CustomAccordion
                    header={<small>Statuses</small>}
                    headerClass="folder-accord-header"
                    contentClass="folder-accord-content"
                    content={
                        <div className='row'>
                            {statuses.map(status => <div className='mr-1'><TaskStatusTemplate status={status} /></div>)}
                        </div>
                    }
                /> : <></>
            }
            {
                teams.length ?
                    <CustomAccordion
                        header={<small>Teams</small>}
                        headerClass="folder-accord-header"
                        contentClass="folder-accord-content"
                        content={
                            <div className='row'>
                                {teams.map(team => <small className='mx-1 px-1 rounded' title={team.description} style={{ border: "1px solid gray" }}>{utils.shorthandString(team.description, 12)}</small>)}
                            </div>
                        }
                    /> : <></>
            }
        </div>
        <div className="card-footer mt-1 d-flex justify-content-between py-1" style={{ fontSize: "12px" }}>
            <code style={{ fontSize: "14px" }}>
                {visibility.toUpperCase()}
            </code>
            <div>
                <IconBtn
                    className="bi bi-pencil mx-1"
                    onClick={editAction} />
                <IconBtn
                    id={folderId}
                    className="bi bi-trash"
                    onClick={() => setDisplayDeleteConfirm(true)} />
                <ConfirmPopup
                    target={document.getElementById(folderId)}
                    visible={displayDeleteConfirm}
                    onHide={() => setDisplayDeleteConfirm(false)}
                    message={CUSTOM_MESSAGES.DELETE_ITEM_CONFIRMATION_MESSAGE}
                    icon={"bi bi-question-circle"}
                    accept={onDeleteTag}
                    reject={() => setDisplayDeleteConfirm(false)} />
            </div>
        </div>

    </div>
}